/**
 * Receptionist knowledge — what the Receptionist may answer when a caller
 * asks something, as opposed to what it asks them.
 *
 * "What it may answer", the last group on Settings → Phone → Receptionist.
 * It was a screen under a drilled-into Receptionist config — four clicks
 * from the sidebar — then two groups on Phone itself, then a section of
 * its own beside it, then a page of its own under Phone, while the
 * Receptionist's switch and schedule stayed on the screen above it. It
 * is one group of the Receptionist's own page now, beside when it
 * answers and the name it gives: what it may answer is only ever
 * answered on a call, and a Company setting the Receptionist up reads
 * the whole of it in one place. It is the Company's and not a number's,
 * so a Company with two numbers finds the same group under either.
 *
 * It keeps its own fetch: the switches are resolved against the rental
 * system, and the shell's company-settings fetch — passed down through
 * Phone — is the box beneath them.
 *
 * Two halves of one decision, which is why they are one screen. The
 * switches say which kinds of question it may answer at all; the box is
 * the only thing it may say about the Company that no rental system
 * holds. With the box blank and every switch off it declines each such
 * question and says the team covers it with the quote, which is what it
 * did before any of this existed.
 *
 * The two halves are under headings that name where each answer comes
 * from, because they were not distinguishable before (#75): "searching
 * your catalogue" was live and true, and "prices from your facts" sat
 * beneath it in the same voice while being an unchecked text box — which
 * is how one Company's callers were told PA tops start at $60 while the
 * same items drafted at $15. So the group says the switches are read
 * **from your rental system**, named, and the box sits apart beneath
 * them.
 *
 * A switch whose `available` is false is shown disabled with the server's
 * reason in place of its description. Nothing here decides that: the
 * ringing-phone path resolves the same pair against the same rental
 * system, so a row reading unavailable cannot be on during a call, and a
 * switch that could not be honoured is never offered and then missed
 * mid-sentence. A dependent row is rendered only while its parent is on,
 * and which row that is comes from the server too (`parent`).
 */
import { Banner } from "../../components/ui/Banner";
import { ToggleSettingRow } from "../../components/ui/SettingRow";
import {
  getReceptionistKnowledge,
  saveReceptionistKnowledge,
  type CompanySettings,
  type CompanySettingsUpdate,
} from "../../lib/settings-api";
import { useSettingsSource } from "../../lib/use-settings-source";
import {
  Loading,
  SettingsGroup,
  TextAreaField,
} from "./section-chrome";
import styles from "./ReceptionistKnowledge.module.css";

export function ReceptionistKnowledge({
  settings,
  onPatch,
}: {
  settings: CompanySettings;
  onPatch: (patch: CompanySettingsUpdate) => Promise<void>;
}) {
  const {
    data: knowledge,
    setData: setKnowledge,
    loadError,
  } = useSettingsSource(getReceptionistKnowledge);

  /** One switch per PUT; the answer is every switch, re-resolved. */
  const flip = async (key: string, enabled: boolean) => {
    setKnowledge(await saveReceptionistKnowledge(key, enabled));
  };

  /**
   * What is written in the box changes the read-back line above the
   * switches — it is the "and anything you have written below" half — so
   * the whole thing is re-read after a save rather than left describing
   * a call that is no longer the one this number takes.
   */
  const saveFacts = async (receptionist_facts: string) => {
    await onPatch({ receptionist_facts });
    setKnowledge(await getReceptionistKnowledge());
  };

  const items = knowledge?.items ?? [];
  const on = new Set(
    items.filter((i) => i.enabled && i.available).map((i) => i.key),
  );
  // A dependent row is rendered only while its parent is on (DESIGN.md).
  const shown = items.filter((i) => !i.parent || on.has(i.parent));

  return (
    <SettingsGroup
      id="receptionist-knowledge"
      title="What it may answer"
      description={
        knowledge
          ? `Read live from ${knowledge.rental_system} on the call.`
          : undefined
      }
    >
      {loadError && (
        <Banner variant="error" className={styles.notice}>
          {loadError}
        </Banner>
      )}

      {/* The read-back, above both halves: one sentence saying what this
          number actually does with a caller's questions, so the setup can
          be checked without reading three rows and working it out. It is
          the server's sentence, built from the switches it resolved. */}
      {knowledge && knowledge.summary !== "" && (
        <p className={styles.summary}>{knowledge.summary}</p>
      )}

      {!knowledge ? (
        !loadError && <Loading />
      ) : (
        shown.map((item) => (
          <ToggleSettingRow
            key={item.key}
            id={`receptionist-knows-${item.key}`}
            title={item.name}
            disabledTooltip={item.tooltip}
            description={
              item.available ? item.description : (item.reason ?? "")
            }
            checked={item.enabled && item.available}
            disabled={!item.available}
            indent={Boolean(item.parent)}
            onChange={(enabled) => flip(item.key, enabled)}
          />
        ))
      )}

      {/* Apart beneath the rows read from the rental system: what is
          typed here is the Company's own word, not a live read (#75). */}
      <TextAreaField
        id="receptionist-facts"
        label="Extra notes"
        tooltip="Extra details the receptionist knows and can use to answer callers."
        value={settings.receptionist_facts}
        rows={14}
        onSave={saveFacts}
      />
    </SettingsGroup>
  );
}
